
import axios from "axios";

const base_url = "https://command.databling.ng/api";
// const base_url = "http://localhost:5000/api";


/* ────────────────────────────────────────────────
   Axios Instance
──────────────────────────────────────────────── */
export const apiClient = axios.create({
  baseURL: base_url,
  headers: {
    "Content-Type": "application/json",
  },
  timeout: 15000,
});

/* ────────────────────────────────────────────────
   Request Interceptor
   Attach Token Automatically
──────────────────────────────────────────────── */
apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("user_token");

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

/* ────────────────────────────────────────────────
   Response Interceptor
   Handle Global Errors
──────────────────────────────────────────────── */
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    const code = error?.response?.data?.code;

    if (
      status === 401 &&
      ["TOKEN_EXPIRED", "INVALID_TOKEN"].includes(code)
    ) {
      localStorage.removeItem("user_token");
      window.location.href = "/login";
    }

    /* Not found (404) */
    if (status === 404) {
      console.error("API endpoint not found:", error?.config?.url);
    }

    /* Server error */
    if (status >= 500) {
      console.error("Server error. Please try again later.");
    }

    return Promise.reject(error);
  }
);

/* ────────────────────────────────────────────────
   Optional API Helpers
   (Cleaner calls in components)
──────────────────────────────────────────────── */

export const api = {
  get: (url, config = {}) => apiClient.get(url, config),
  post: (url, data = {}, config = {}) => apiClient.post(url, data, config),
  patch: (url, data = {}, config = {}) => apiClient.patch(url, data, config),
  put: (url, data = {}, config = {}) => apiClient.put(url, data, config),
  delete: (url, config = {}) => apiClient.delete(url, config),
};


// Request OTP endpoint
export const register = async (payload) => {
  try {
    // const payload = { email, phone };
    const response = await apiClient.post("/auth/register", payload);
    return response.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

// Verify OTP endpoint
export const verifyOTP = async (email, otp) => {
  try {
    const response = await apiClient.post("/auth/verify-otp", {
      email,
      otp,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

export const resendOTP = async (email, otp) => {
  try {
    const response = await apiClient.post("/auth/otp/resend", {
      email,
      otp,
    });
    return response.data;
  } catch (error) {
    throw error;
  }
};

// Set password endpoint
export const setPassword = async (email, password, referralCode) => {
  try {
    const payload = { email, password };
    if (referralCode) {
      payload.referralCode = referralCode;
    }
    const response = await apiClient.post(
      "/auth/set-password",
      payload
    );
    return response.data;
  } catch (error) {
    console.log(error);
    throw error;
  }
};

// Login endpoint
export const loginUser = async (email, password) => {
  try {
    const payload = { email, password };
    const response = await apiClient.post("/auth/login", payload);
    return response.data;
  } catch (error) {
    console.log("Response data:", error);
    throw error;
  }
};

export const requestPasswordReset = async (email) => {
  try {
    const response = await apiClient.post('/auth/forgot-password', { email });
    return response.data;
  } catch (error) {
    console.log("Error:", error)
    throw error.response?.data || { message: "Failed to send reset code" };
  }
};

export const verifyResetOtp = async (email, otp) => {
  try {
    const response = await apiClient.post('/auth/forgot-password/verify-otp', {
      email,
      otp: otp.trim(),
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: "Invalid or expired OTP" };
  }
};

export const resetPassword = async (email, newPassword) => {
  try {
    const response = await apiClient.post('/auth/forgot-password/reset', {
      email,
      newPassword,
    });
    return response.data;
  } catch (error) {
    throw error.response?.data || { message: "Failed to reset password" };
  }
};

export const fetchUserInfo = async () => {
  try {
    const res = await apiClient.get('/users/me');
  return res.data;
  } catch (error) {
    console.log("Failed to fetch user info:", error.response?.data || error.message);
    throw error;
  }
};

export const updateUserInfo = async (data) => {
  try {
    const res = await apiClient.patch('/users/me', data);

    return res.data;
  } catch (error) {
    console.log("Failed to update user info:", error.response?.data || error.message);
    throw error;
  }
};

export const changePassword = async ({ currentPassword, newPassword }) => {
  try {
    const res = await apiClient.post("/auth/change-password", {
      currentPassword,
      newPassword,
    });

    return res.data;
  } catch (error) {
    const err = error.response?.data || { message: "Network error" };
    throw new Error(err.message || "Failed to change password");
  }
};


export const paystackInitialize = async (amount) => {
  try {
    const res = await apiClient.post(`/funding/paystack/initialize`, {amount});
    return res.data;
  } catch (error) {
    console.log("Failed to initialize:", error.response?.data || error.message);
    throw error;
  }
};

export const verifyPaystack = async (reference) => {
  try {
    const res = await apiClient.get(`/funding/paystack/verify/${reference}`);
    return res.data;
  } catch (error) {
    console.log("Failed to verify payment:", error.response?.data || error.message);
    throw error;
  }
};

export const getMyWallet = async () => {
  try {
    const res = await apiClient.get(`/mywallet`);
    return res.data;
  } catch (error) {
    console.log("Failed to fetch wallet:", error.response?.data || error.message);
    throw error;
  }
};

export const getAirtimeNetworks = async () => {
  try {
    const res = await apiClient.get(`/vtu/airtime/networks`);
    return res.data;
  } catch (error) {
    console.log("Failed to airtime networks", error.response?.data || error.message);
    throw error;
  }
};

export const getDataNetworks = async () => {
  try {
    const res = await apiClient.get(`/vtu/data/networks`);
    return res.data;
  } catch (error) {
    console.log("Failed to Data networks", error.response?.data || error.message);
    throw error;
  }
};

export const getDataPlans = async (networkIdentifier) => {
  try {
    const res = await apiClient.get(`/vtu/data/plans?network=${networkIdentifier}`);
    return res.data;
  } catch (error) {
    console.log("Failed to fetch data plans", error.response?.data || error.message);
    throw error;
  }
};

export const createPin = async (pin) => {
  try {
    const res = await apiClient.post(`/auth/pin/set`, {pin});
    return res.data;
  } catch (error) {
    console.log("Failed to create pin", error.response?.data || error.message);
    throw error;
  }
};

export const updatePin = async (currentPin, newPin) => {
  try {
    const payload = {currentPin, newPin}
    const res = await apiClient.post(`/auth/pin/update`, payload);
    return res.data;
  } catch (error) {
    console.log("Failed to update pin", error.response?.data || error.message);
    throw error;
  }
};

export const buyAirtime = async (phone, network, amount, pin) => {
  try {
    const payload = {phone, network, amount, pin}
    const res = await apiClient.post(`/vtu/top/airtime`, payload);
    return res.data;
  } catch (error) {
    console.log("Failed to buy airtime", error.response?.data || error.message);
    throw error;
  }
};

export const buyData = async (mobile_number, network, plan_code, amount, pin) => {
  try {
    const payload = {mobile_number, network, plan_code, amount, pin}
    const res = await apiClient.post(`/vtu/data`, payload);
    return res.data;
  } catch (error) {
    console.log("Failed to buy data", error.response?.data || error.message);
    throw error;
  }
};

export const buyTopData = async (phone, plan, amount, pin) => {
  try {
  const res = await apiClient.post('/vtu/top/data', { phone, plan, amount, pin });
   return res.data;
  } catch (error) {
    console.log("Failed to buy top data", error.response?.data || error.message);
    throw error;
  }
};

export const getTopDataOffers = async () => {
  try {
    const res = await apiClient.get(`/vtu/top/data/plans`);
    return res.data;
  } catch (error) {
    console.log("Failed to fetch top data: ", error.response?.data || error.message);
    throw error;
  }
};

export const buyTopAirtimeOffers = async () => {
  try {
    const res = await apiClient.post(`/vtu/top/airtime`);
    return res.data;
  } catch (error) {
    console.log("Failed to buy top airtime: ", error.response?.data || error.message);
    throw error;
  }
};

export const buyTopDataOffers = async () => {
  try {
    const res = await apiClient.post(`/vtu/top/data`);
    return res.data;
  } catch (error) {
    console.log("Failed to buy top data: ", error.response?.data || error.message);
    throw error;
  }
};

export const fetchTransactionsHistory = async (params = {}) => {
  try {
    // Build query string only for defined values
    const query = new URLSearchParams();

    if (params.status && params.status !== "All Status") {
      query.append("status", params.status.toLowerCase());
    }
    if (params.category && params.category !== "All Categories") {
      query.append("category", params.category.toLowerCase());
    }
    if (params.month)  query.append("month", params.month);
    if (params.year)   query.append("year", params.year);
    if (params.from)   query.append("from", params.from);
    if (params.to)     query.append("to", params.to);
    if (params.page)   query.append("page", params.page);
    if (params.limit)  query.append("limit", params.limit || 20);

    const queryString = query.toString();
    const url = `/transactions/history${queryString ? `?${queryString}` : ""}`;

    const res = await apiClient.get(url);
    return res.data; 
  } catch (error) {
    console.error("Failed to fetch transaction history:", error.response?.data || error.message);
    throw error;
  }
};

export const getReferrals = async () => {
  try {
    const res = await apiClient.get(`/referrals`);
    return res.data;
  } catch (error) {
    console.log("Failed to fetch referrals: ", error.response?.data || error.message);
    throw error;
  }
};

export const getReferralSummary = async () => {
  try {
    const res = await apiClient.get(`/referrals/summary`);
    return res.data;
  } catch (error) {
    console.log("Failed to fetch referral summary: ", error.response?.data || error.message);
    throw error;
  }
};

export const getCashbackBalance = async () => {
  try {
    const res = await apiClient.get(`/vtu/cashback/balance`);
    return res.data;
  } catch (error) {
    console.log("Failed to fetch Cashback Balance: ", error.response?.data || error.message);
    throw error;
  }
};

export const reportTransaction = async (transactionReference, amount, paymentProvider, paymentMethod, description, proofUrl) => {
  try {
    const payload = {transactionReference, amount, paymentProvider, paymentMethod, description, proofUrl}
    const res = await apiClient.post("/transactions/report-failed", payload);
    return res.data;
  } catch (err) {
    console.error("Error:", err.response?.data || err.message);
    throw err;
  }
};

export const getMyFailedTransactions = async () => {
  try {
    const res = await apiClient.get("/transactions/my-reports");
    return res.data;
  } catch (err) {
    console.error("Error:", err.response?.data || err.message);
    throw err;
  }
};

export const generateDVA = async (preferredBank) => {
  try {
    const res = await apiClient.post("/funding/virtual-account/create", {preferredBank});
    return res.data;
  } catch (err) {
    console.error("Error:", err.response?.data || err.message);
    throw err;
  }
};

export const getDVA = async () => {
  try {
    const res = await apiClient.get("/funding/virtual-account");
    return res.data;
  } catch (err) {
    console.error("Error:", err.response?.data || err.message);
    throw err;
  }
};


export const registerNotificationToken = (token) =>
  apiClient.post("/notifications/token/register", { token, platform: "web" });

export const removeNotificationToken = (token) =>
  apiClient.post("/notifications/token/remove", { token });

// ── Notifications list ────────────────────────────────────────────────────────

export const getNotifications = (page = 1, limit = 20) =>
  apiClient.get("/notifications", { params: { page, limit } });

// ── Mark as read ──────────────────────────────────────────────────────────────

export const markNotificationRead = (id) =>
  apiClient.patch(`/notifications/${id}/read`);

export const markAllNotificationsRead = () =>
  apiClient.patch("/notifications/read-all");



/* ────────────────────────────────────────────────
   Electricity Payment Endpoints
──────────────────────────────────────────────── */

/**
 * Fetch available electricity distribution companies (Discos) and plans
 */
export const getElectricityPlans = async () => {
  try {
    const res = await apiClient.get("/bills/electricity/plans");
    return res.data;
  } catch (error) {
    console.error("Failed to fetch electricity plans:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Verify customer meter details
 * @param {Object} payload - { disco, meterNumber, meterType }
 */
export const verifyMeter = async (payload) => {
  try {
    const res = await apiClient.post("/bills/electricity/verify", payload);
    return res.data;
  } catch (error) {
    console.error("Failed to verify meter:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Purchase electricity units
 * @param {Object} payload - { disco, meterNumber, meterType, amount, pin }
 */
export const buyElectricity = async (payload) => {
  try {
    const res = await apiClient.post("/bills/electricity/buy", payload);
    return res.data;
  } catch (error) {
    console.error("Failed to purchase electricity:", error.response?.data || error.message);
    throw error;
  }
};


/* ────────────────────────────────────────────────
   Cable TV Subscription Endpoints
──────────────────────────────────────────────── */

/**
 * Fetch available cable networks and bouquet plans
 * @param {string} provider - 'dstv', 'gotv', or 'startimes'
 */
export const getCablePlans = async (provider = "dstv") => {
  try {
    const res = await apiClient.get(`/bills/tv/plans?provider=${provider}`);
    return res.data;
  } catch (error) {
    console.error(`Failed to fetch ${provider} cable plans:`, error.response?.data || error.message);
    throw error;
  }
};

/**
 * Verify cable smart card / IUC number details
 * @param {Object} payload - { provider, smartcardNo }
 */
export const verifyCableCard = async (payload) => {
  try {
    const res = await apiClient.post("/bills/tv/verify", payload);
    return res.data;
  } catch (error) {
    console.error("Failed to verify cable card:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Subscribe to cable package bouquet
 * @param {Object} payload - { provider, smartcardNo, variationCode, amount, pin }
 */
export const buyCable = async (payload) => {
  try {
    const res = await apiClient.post("/bills/tv/subscribe", payload);
    return res.data;
  } catch (error) {
    console.error("Failed to purchase cable subscription:", error.response?.data || error.message);
    throw error;
  }
};


/* ────────────────────────────────────────────────
   Exam E-Pin Endpoints
──────────────────────────────────────────────── */

/**
 * Fetch exam pin types (WAEC, NECO, JAMB, etc.) and prices
 */
export const getEpinPlans = async () => {
  try {
    const res = await apiClient.get("/bills/exam-pins/products");
    return res.data;
  } catch (error) {
    console.error("Failed to fetch exam pin plans:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Purchase exam pin checker vouchers
 * @param {Object} payload - { cardTypeId, quantity, amount, pin }
 */
export const buyEpin = async (payload) => {
  try {
    const res = await apiClient.post("/bills/exam-pins/buy", payload);
    return res.data;
  } catch (error) {
    console.error("Failed to purchase exam pins:", error.response?.data || error.message);
    throw error;
  }
};


/* ────────────────────────────────────────────────
   Platform Settings (admin-controlled)
   Lets the app know — without any hardcoded config of its own — whether
   "Continue with Google" should show, which channel registration OTPs go
   out on, and the current wallet funding fee, all of which the admin can
   flip on/off from the dashboard.
──────────────────────────────────────────────── */

export const getPublicSettings = async () => {
  try {
    const res = await apiClient.get("/settings");
    return res.data;
  } catch (error) {
    console.log("Failed to fetch platform settings:", error.response?.data || error.message);
    throw error;
  }
};

/**
 * Exchange a Google ID token for a DataBling session. Works for both new
 * signups and returning users — the backend decides which based on
 * whether the Google account is already linked.
 */
export const googleLogin = async (idToken) => {
  try {
    const res = await apiClient.post("/auth/google", { idToken });
    return res.data;
  } catch (error) {
    console.log("Google sign-in failed:", error.response?.data || error.message);
    throw error;
  }
};